import { useCallback, useEffect, useMemo } from 'react';

import {
  useCreateConsumerFamilyBacker,
  useShowConsumerFamily,
  useUpdateConsumerFamilyBacker,
} from '@givebacks/api/backer';
import { useForm, useFormyValidator } from '@givebacks/formy';
import {
  Alert,
  Button,
  Grid,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
  Tooltip,
  notifications,
} from 'ui';
import { UseFormReturnType, combineValidators, isEmail, isNotEmpty } from 'ui/Form';
import { IconInfo } from 'ui/Icons';

import { getErrorMessage_v2 } from '../../../../../utils/errorMessage';
import {
  ModalTitleWithBackButton,
  useDirectorySettingsModalProvider,
} from '../DirectorySettingsModalProvider';

type AdultProfileForm = {
  firstName: string;
  lastName: string;
  email?: string;
  isAccountClaimed: boolean;
};

const AddEditAdultModalFooter = () => {
  const { mutate: createConsumerFamilyBacker } = useCreateConsumerFamilyBacker();
  const { mutate: updateConsumerFamilyBacker } = useUpdateConsumerFamilyBacker();
  const { openBack, openModalById, meta } = useDirectorySettingsModalProvider({});

  const { useGetForm } = useFormyValidator<{
    adultProfile: UseFormReturnType<AdultProfileForm>;
  }>();
  const form = useGetForm('adultProfile');

  const handleAddAdult = useCallback(() => {
    if (!meta.familyId) return;
    if (!form?.validate().hasErrors) {
      createConsumerFamilyBacker(
        {
          familyId: meta.familyId,
          backer: {
            first_name: form.values.firstName,
            last_name: form.values.lastName,
            email: form.values.email,
          },
        },
        {
          onSuccess: () => {
            notifications.show({
              message: `${form.values.firstName} ${form.values.lastName} added to family`,
              variant: 'success',
            });
            openBack();
          },
          onError: (error) => {
            notifications.show({
              message: getErrorMessage_v2(error),
              variant: 'error',
            });
          },
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form?.values?.firstName,
    form?.values?.lastName,
    form?.values?.email,
    meta.familyId,
    createConsumerFamilyBacker,
    openBack,
  ]);

  const handleSave = useCallback(() => {
    if (!form?.validate().hasErrors) {
      if (!meta.familyId || !meta.backerId) return;
      updateConsumerFamilyBacker(
        {
          familyId: meta.familyId,
          backerId: meta.backerId,
          backer: {
            first_name: form.values.firstName,
            last_name: form.values.lastName,
            email: form.values.email,
          },
        },
        {
          onSuccess: () => {
            notifications.show({
              message: `${form.values.firstName} ${form.values.lastName} saved`,
              variant: 'success',
            });
            openBack();
          },
          onError: (error) => {
            notifications.show({
              message: getErrorMessage_v2(error),
              variant: 'error',
            });
          },
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form?.values?.firstName,
    form?.values?.lastName,
    form?.values?.email,
    meta.familyId,
    meta.backerId,
    updateConsumerFamilyBacker,
    openBack,
  ]);

  return (
    <Modal.AffixedFooter>
      {meta.backerId ? (
        <Stack gap="sm">
          {!form?.values?.isAccountClaimed && (
            <Button fullWidth onClick={handleSave}>
              Save Changes
            </Button>
          )}
          <Button
            color="cerise"
            variant="outline"
            fullWidth
            onClick={() => openModalById('remove-adult')}
          >
            Remove Adult
          </Button>
        </Stack>
      ) : (
        <Button fullWidth onClick={handleAddAdult}>
          Add Adult
        </Button>
      )}
    </Modal.AffixedFooter>
  );
};

const AddEditAdultForm = () => {
  const { meta } = useDirectorySettingsModalProvider({});

  const { data: backer } = useShowConsumerFamily({
    familyId: meta.familyId,
    include: 'backers',
    select: (data) => data.backers?.find((backer) => backer.uuid === meta.backerId),
  });

  const form = useForm<AdultProfileForm>(
    {
      initialValues: {
        firstName: '',
        lastName: '',
        email: '',
        isAccountClaimed: false,
      },
      validate: {
        firstName: isNotEmpty('First name is required'),
        lastName: isNotEmpty('Last name is required'),
        email: combineValidators(isNotEmpty('Email is required'), isEmail('Invalid email format')),
      },
      enhanceGetInputProps: () => ({
        readOnly: backer?.has_login ?? false,
      }),
    },
    { id: 'adultProfile' }
  );

  const isNewAdult = useMemo(() => {
    return !backer;
  }, [backer]);

  const hasClaimedAccount = useMemo(() => {
    return backer && backer?.has_login;
  }, [backer]);

  const showEmailTooltip = useMemo(() => {
    return backer && !hasClaimedAccount;
  }, [backer, hasClaimedAccount]);

  useEffect(() => {
    if (backer) {
      form.initialize({
        firstName: backer.first_name,
        lastName: backer.last_name,
        email: backer.email,
        isAccountClaimed: backer.has_login ?? false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backer]);

  return (
    <Stack gap="lg">
      {/**
       * Show form message if adding a new adult or the account has not yet been claimed
       */}
      {!hasClaimedAccount && (
        <Text fz="xs" c="gray">
          {!isNewAdult
            ? 'The changes you make will be reflected in your organizations’ directories.'
            : 'Add an adult you’d like displayed in your organizations’ directories.'}
        </Text>
      )}

      {/**
       * Show an alert to clarify readonly form fields
       */}
      {hasClaimedAccount && (
        <Alert type="info-blue">This account has been claimed and cannot be edited.</Alert>
      )}
      {/**
       * Show an alert to encourage accurate information to be entered
       */}
      {isNewAdult && (
        <Alert title="Verify email address" type="info-blue">
          An email notification will be sent to the address you enter. Please verify that the email
          address is accurate before proceeding.
        </Alert>
      )}

      <Grid>
        <Grid.Col span={6}>
          <TextInput label="First Name" {...form.getInputProps('firstName')} required />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput label="Last Name" {...form.getInputProps('lastName')} required />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput
            label={
              showEmailTooltip ? (
                <Group justify="space-between">
                  <Text type="label">Email</Text>
                  <Tooltip
                    label="An email notification has been sent to this email. If this is incorrect, please remove this family member."
                    w={300}
                  >
                    <IconInfo size={16} />
                  </Tooltip>
                </Group>
              ) : (
                'Email'
              )
            }
            required={!showEmailTooltip}
            {...form.getInputProps('email')}
            readOnly={!!backer}
          />
        </Grid.Col>
      </Grid>
    </Stack>
  );
};

const AddEditAdultModal = () => {
  const { opened, close, meta } = useDirectorySettingsModalProvider({
    id: 'backer-profile',
  });

  const title = useMemo(() => {
    return meta.backerId ? 'Edit Profile' : 'Add Adult';
  }, [meta.backerId]);

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={<ModalTitleWithBackButton>{title}</ModalTitleWithBackButton>}
    >
      <AddEditAdultForm />
      <AddEditAdultModalFooter />
    </Modal>
  );
};

export { AddEditAdultModal };
